import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getCancellationTimeline } from '../utils/cancellationSlabs.js';
import './CancellationRefundTimeline.css';

/**
 * @param {{ departureDate?: string|Date|null, totalAmount?: number|null, refundSlabs?: Array<{daysBefore:number,refundPercent:number}>|null, compact?: boolean, className?: string }} props
 */
export default function CancellationRefundTimeline({
  departureDate,
  totalAmount = null,
  refundSlabs = null,
  compact = false,
  className = ''
}) {
  const timeline = useMemo(
    () =>
      getCancellationTimeline(departureDate, {
        totalAmount: totalAmount != null ? Number(totalAmount) : undefined,
        refundSlabs: Array.isArray(refundSlabs) && refundSlabs.length ? refundSlabs : undefined
      }),
    [departureDate, totalAmount, refundSlabs]
  );

  const { slabs, currentIndex, summaryMessage, departureHeading } = timeline;
  const currentSlabRef = useRef(null);

  useEffect(() => {
    currentSlabRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [currentIndex, departureDate]);

  return (
    <div className={`cancel-refund-timeline-wrap ${compact ? 'cancel-refund-timeline-wrap--compact' : ''} ${className}`.trim()}>
      <h3 className="cancel-refund-title">Cancellations &amp; Refunds</h3>
      <p className="cancel-refund-intro">
        {departureHeading
          ? `Slabs for your departure on ${departureHeading}:`
          : 'Cancellation slabs are calculated from your trek departure date:'}
      </p>
      <div className="cancel-refund-current" role="status">
        <span className="cancel-refund-current-label">Today</span>
        <p className="cancel-refund-current-text">{summaryMessage}</p>
      </div>
      <div className="cancel-refund-timeline">
        {slabs.map((slab, idx) => {
          const isCurrent = idx === currentIndex;
          const isPast = idx < currentIndex;
          return (
            <div
              key={idx}
              ref={isCurrent ? currentSlabRef : undefined}
              className={[
                'cancel-refund-slab',
                isCurrent ? 'is-current' : '',
                isPast ? 'is-past' : '',
                !isCurrent && idx > currentIndex ? 'is-future' : '',
                slab.noRefund ? 'no-refund' : ''
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <div
                className="cancel-refund-dot"
                aria-hidden={!isCurrent}
                aria-label={isCurrent ? 'Your current cancellation window' : undefined}
              >
                {isCurrent ? <span className="cancel-refund-dot-mark">×</span> : null}
              </div>
              {isCurrent ? <span className="cancel-refund-you-are-here">You are here</span> : null}
              <p className="cancel-refund-slab-label">{slab.label}</p>
              <p className={`cancel-refund-slab-text ${slab.noRefund ? 'no-refund' : ''}`}>{slab.text}</p>
              {slab.note ? <p className="cancel-refund-slab-note">{slab.note}</p> : null}
            </div>
          );
        })}
      </div>
      <p className="cancel-refund-footnote">
        Booking amount is non-refundable. See the{' '}
        <Link to="/terms" className="cancel-refund-terms-link">
          Terms &amp; Conditions
        </Link>{' '}
        for the full policy.
      </p>
    </div>
  );
}
